/**
 * One run of one command in the command shell (04_frontend_spec.md §5.9):
 * the inputs, the dry run kept current as they change, the choices and
 * confirmations it asks for, and the commit.
 *
 * Framework-free so it is testable without React; `useCommandRun` subscribes
 * to it. The rules it holds:
 *
 * - The dry run runs on open, at once on a discrete change (a choice, a tick,
 *   a select) and 300 ms after typing stops. Each run supersedes the last: the
 *   older request is aborted and any answer that is not the latest is dropped.
 * - A choice's options keep the position they first appeared in while the run is open: a later answer may re-sort them,
 *   but what the person sees does not move.
 * - A choice's key is a request-body path. A newly offered choice takes its
 *   default; a choice the dry run stops offering leaves the body.
 * - Paths the caller locks (a related list's params: the note's subject) start
 *   in the body, go out with every dry run and the commit, and cannot be set.
 * - The dry run's `prefill` (starting values for this record) fills the body
 *   once, on the first answer, without overwriting anything typed.
 * - Errors show on fields the person has touched until the first commit
 *   attempt, then everywhere; an error with no field shows at once. Paths are translated back through table-row
 *   selection: unticked rows are left out of the body, so `lines.1.amount`
 *   may be the UI's third row. The same holds one level down, for the rows
 *   nested in a row (`lines.1.destinations.0.quantity`); see `table-rows.ts`.
 * - Untouched is not the same as empty: an error on an untouched field that
 *   already holds a value (a prefill, a default) shows too. Only the errors on
 *   empty untouched inputs wait, and `unfilledFields` names those inputs so a
 *   disabled commit always has a reason on screen.
 * - The commit carries the inputs, the choices, the guard, `confirmed` and a
 *   `request_id` minted on the first press and kept while the body is
 *   unchanged (a retry after a failure repeats it, never double-applies).
 *   A 409 stale re-runs the dry run; 400/422 bind to the fields.
 */
import { ApiError } from "../../data/api/errors";
import type {
  CommandBody,
  CommandEndpoint,
  ObjectUiAdapter,
} from "../../data/object-ui-adapter";
import { extractFieldErrors } from "../../data/validation";
import { deleteAt, getAt, setAt } from "./body-path";
import {
  CHOICE_SEARCH_FIELD,
  isChosenFile,
  type ChoiceValue,
  type CommandResult,
  type DryRun,
  type FieldError,
} from "./contract";
import { commandTitle } from "./manifest";
import { uiPathOf, wireTableRows, type TableAt } from "./table-rows";
import { humanise } from "./values";
import {
  allFieldsOf,
  bodyReader,
  fieldsOf,
  isLockedOn,
  isShown,
  soleOptionValues,
  withFieldHints,
  type FieldDescriptor,
} from "./widgets";

export const DRY_RUN_DEBOUNCE_MS = 300;

function isEmptyValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}
/** How many dry runs in a row a pick the answer no longer offers may set off. */
const MAX_PICK_RECHECKS = 3;

export type CommandRunStatus =
  "checking" | "checked" | "check_failed" | "committing" | "done";

export interface CommandRunBanner {
  tone: "error" | "warning";
  message: string;
}

export interface CommandRunSnapshot {
  /** The inputs as the UI holds them (every table row, ticked or not). */
  body: CommandBody;
  fields: FieldDescriptor[];
  dryRun: DryRun | null;
  status: CommandRunStatus;
  /** A dry run is scheduled or in flight: what is shown may be one change behind. */
  isPending: boolean;
  /** How many dry runs have been asked for. */
  seq: number;
  choices: Readonly<Record<string, ChoiceValue>>;
  /** What is typed in each searchable choice's search box, by choice key. */
  searches: Readonly<Record<string, string>>;
  confirmed: ReadonlySet<string>;
  /** Body paths that cannot be edited here. */
  lockedPaths: readonly string[];
  /** Ticked rows per selectable table path (`lines`, and `lines.2.destinations` for a nested one). */
  selectedRows: Readonly<Record<string, readonly number[]>>;
  /** The errors to show now, on UI paths. */
  errors: FieldError[];
  /** Labels of the empty, untouched fields whose errors are held back: what the person still has to fill in. */
  unfilledFields: string[];
  banner: CommandRunBanner | null;
  result: CommandResult | null;
  canCommit: boolean;
}

export interface CommandRunOptions {
  adapter: ObjectUiAdapter;
  endpoint: CommandEndpoint;
  /** Starting values the caller knows (a bulk command's targets). */
  initialBody?: CommandBody;
  /** Body paths in `initialBody` that came from the record the command was opened from: shown, not editable. */
  lockedPaths?: readonly string[];
  /** Top-level body paths a custom widget owns: not drawn as fields. */
  ownedPaths?: readonly string[];
  debounceMs?: number;
  newRequestId?: () => string;
}

/** A client-generated UUID: a commit's `request_id`, an upload's `upload_id`. */
export function newUuid(): string {
  const crypto = (globalThis as { crypto?: { randomUUID?: () => string } })
    .crypto;
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function isAbort(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

function staleMessage(err: ApiError): string | null {
  if (err.status !== 409) return null;
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === "object") {
    const { code, message } = detail as { code?: unknown; message?: unknown };
    if (code === "stale" && typeof message === "string") return message;
  }
  return null;
}

function conflictMessage(err: ApiError): string {
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (
    detail &&
    typeof detail === "object" &&
    typeof (detail as { message?: unknown }).message === "string"
  )
    return (detail as { message: string }).message;
  return err.detail || "It can't be done now.";
}

export class CommandRun {
  private readonly adapter: ObjectUiAdapter;
  readonly endpoint: CommandEndpoint;
  private readonly ownedPaths: readonly string[];
  private readonly lockedPaths: readonly string[];
  private readonly debounceMs: number;
  private readonly newRequestId: () => string;
  private readonly listeners = new Set<() => void>();

  private body: CommandBody;
  private touched = new Set<string>();
  private showAllErrors = false;
  private choices: Record<string, ChoiceValue> = {};
  private confirmed = new Set<string>();
  private selectedRows: Record<string, number[]> = {};
  private dryRun: DryRun | null = null;
  /** Each choice's option values in the order they first appeared (see `withStableOptionOrder`). */
  private optionOrder: Record<string, string[]> = {};
  private status: CommandRunStatus = "checking";
  private seq = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: AbortController | null = null;
  private isPrefilled = false;
  /** Dry runs in a row re-run because a pick was no longer offered. */
  private pickRechecks = 0;
  private commitErrors: FieldError[] | null = null;
  private banner: CommandRunBanner | null = null;
  private result: CommandResult | null = null;
  private requestId: { id: string; wire: string } | null = null;
  private snapshot: CommandRunSnapshot;
  private isDisposed = false;

  constructor(options: CommandRunOptions) {
    this.adapter = options.adapter;
    this.endpoint = options.endpoint;
    this.ownedPaths = options.ownedPaths ?? [];
    this.lockedPaths = options.lockedPaths ?? [];
    this.debounceMs = options.debounceMs ?? DRY_RUN_DEBOUNCE_MS;
    this.newRequestId = options.newRequestId ?? newUuid;
    this.body = { ...(options.initialBody ?? {}) };
    // A select with one option has nothing to choose, so it is drawn as text: the body holds its value for it.
    for (const [path, value] of Object.entries(
      soleOptionValues(this.fields()),
    )) {
      if (getAt(this.body, path) === undefined)
        this.body = setAt(this.body, path, value);
    }
    this.snapshot = this.buildSnapshot();
  }

  // ── Subscription (useSyncExternalStore) ─────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): CommandRunSnapshot => this.snapshot;

  private emit(): void {
    this.snapshot = this.buildSnapshot();
    this.listeners.forEach((l) => l());
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  /** Run the first dry run (again after a dispose: React's strict mode mounts twice). */
  start(): void {
    this.isDisposed = false;
    this.runDryRun();
  }

  /** Stop: no timer fires and no answer lands. Subscribers unsubscribe themselves. */
  dispose(): void {
    this.isDisposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.inFlight?.abort();
    this.inFlight = null;
  }

  // ── Inputs ─────────────────────────────────────────────────────────────

  /** Set an input. `debounce` for typing; discrete changes check at once. */
  setValue(
    path: string,
    value: unknown,
    options: { debounce?: boolean } = {},
  ): void {
    if (this.isLocked(path)) return;
    this.body =
      value === undefined
        ? deleteAt(this.body, path)
        : setAt(this.body, path, value);
    this.touch(path);
    this.afterChange(options.debounce === true);
  }

  /**
   * Several inputs at once (a custom widget's value). With `refill`, the paths become starting values again: they leave
   * the body untouched, and the next dry run's prefill fills them, as it did on the first answer. A widget uses it when
   * the person picks another record, whose starting values replace the last record's.
   */
  setValues(
    values: Record<string, unknown>,
    options: { debounce?: boolean; refill?: boolean } = {},
  ): void {
    for (const [path, value] of Object.entries(values)) {
      if (this.isLocked(path)) continue;
      this.body =
        value === undefined
          ? deleteAt(this.body, path)
          : setAt(this.body, path, value);
      if (options.refill) {
        this.touched.delete(path);
        this.touched.delete(path.split(".")[0]!);
      } else this.touch(path);
    }
    if (options.refill) {
      this.isPrefilled = false;
      this.commitErrors = null;
      this.banner = null;
    }
    this.afterChange(options.debounce === true);
  }

  private isLocked(path: string): boolean {
    return this.lockedPaths.includes(path.split(".")[0]!);
  }

  choose(key: string, value: string): void {
    const choice = this.dryRun?.choices.find((c) => c.key === key);
    if (!choice || choice.multiple) return;
    const option = choice.options.find((o) => o.value === value);
    if (!option || option.disabled_reason) return;
    this.choices = { ...this.choices, [key]: value };
    this.touch(key);
    this.afterChange(false);
  }

  /** A multiple choice's picks, in the options' order; a value not offered, or disabled, is dropped. */
  chooseMany(key: string, values: readonly string[]): void {
    const choice = this.dryRun?.choices.find((c) => c.key === key);
    if (!choice?.multiple) return;
    const picked = choice.options
      .filter(
        (o) =>
          (!o.disabled_reason || isLockedOn(o)) &&
          (isLockedOn(o) || values.includes(o.value)),
      )
      .map((o) => o.value);
    this.choices = { ...this.choices, [key]: picked };
    this.touch(key);
    this.afterChange(false);
  }

  /**
   * Text typed in a searchable choice's search box: it goes back with the next dry run under `search.<key>`, after the
   * usual typing pause, and the owner answers with the options that match.
   */
  setSearch(key: string, text: string): void {
    this.body = {
      ...this.body,
      [CHOICE_SEARCH_FIELD]: { ...this.searchesOf(), [key]: text },
    };
    this.afterChange(true);
  }

  private searchesOf(): Record<string, string> {
    const held = this.body[CHOICE_SEARCH_FIELD];
    return held && typeof held === "object"
      ? (held as Record<string, string>)
      : {};
  }

  setConfirmed(key: string, isConfirmed: boolean): void {
    const next = new Set(this.confirmed);
    if (isConfirmed) next.add(key);
    else next.delete(key);
    this.confirmed = next;
    this.emit();
  }

  setRowSelected(tablePath: string, row: number, isSelected: boolean): void {
    const current = new Set(this.selectedRows[tablePath] ?? []);
    if (isSelected) current.add(row);
    else current.delete(row);
    this.selectedRows = {
      ...this.selectedRows,
      [tablePath]: [...current].sort((a, b) => a - b),
    };
    this.touch(tablePath);
    this.afterChange(false);
  }

  /**
   * Values chosen by something other than the inputs (the best route priced so far, from a pending-prices block): a
   * value for a key the dry run offers as a choice is that choice's pick, the rest are set on the body. One check
   * follows.
   */
  setChosen(values: Record<string, string>): void {
    for (const [key, value] of Object.entries(values)) {
      const choice = this.dryRun?.choices.find((c) => c.key === key);
      const isOffered =
        choice !== undefined &&
        !choice.multiple &&
        choice.options.some((o) => o.value === value && !o.disabled_reason);
      if (isOffered) this.choices = { ...this.choices, [key]: value };
      else if (!this.isLocked(key)) this.body = setAt(this.body, key, value);
      this.touch(key);
    }
    this.afterChange(false);
  }

  /** Check again after a dry run that could not be read. */
  retry(): void {
    this.runDryRun();
  }

  private touch(path: string): void {
    this.touched.add(path);
    this.touched.add(path.split(".")[0]!);
    this.commitErrors = null;
    this.banner = null;
  }

  private afterChange(debounce: boolean): void {
    if (this.timer) clearTimeout(this.timer);
    if (debounce) {
      this.status = "checking";
      this.timer = setTimeout(() => {
        this.timer = null;
        this.runDryRun();
      }, this.debounceMs);
      this.emit();
    } else {
      this.timer = null;
      this.runDryRun();
    }
  }

  // ── The body on the wire ───────────────────────────────────────────────

  private fields(): FieldDescriptor[] {
    const hidden = new Set<string>(this.ownedPaths);
    for (const choice of this.dryRun?.choices ?? [])
      hidden.add(choice.key.split(".")[0]!);
    for (const key of Object.keys(this.dryRun?.guard ?? {})) hidden.add(key);
    return withFieldHints(
      fieldsOf(this.endpoint.command.request_schema, { hidden }),
      this.dryRun?.field_hints ?? {},
    );
  }

  private tables(fields: FieldDescriptor[]): TableAt[] {
    return fields.flatMap((f) =>
      f.kind === "table" ? [{ path: f.path, table: f.table }] : [],
    );
  }

  /** The body as sent: hidden fields out, unticked rows out, choices in. */
  private wireBody(fields: FieldDescriptor[]): CommandBody {
    let wire: CommandBody = { ...this.body };
    const read = bodyReader(this.body, this.choices);
    for (const field of allFieldsOf(fields)) {
      if (!isShown(field, read)) wire = deleteAt(wire, field.path);
    }
    for (const table of this.tables(fields)) {
      const field = fields.find((f) => f.path === table.path)!;
      const rows = getAt(this.body, table.path);
      if (!Array.isArray(rows) || !isShown(field, read)) continue;
      wire = setAt(
        wire,
        table.path,
        wireTableRows(table, rows, this.selectedRows),
      );
    }
    for (const [key, value] of Object.entries(this.choices))
      wire = setAt(wire, key, value);
    return wire;
  }

  private hasProperty(name: string): boolean {
    return Boolean(this.endpoint.command.request_schema?.properties?.[name]);
  }

  private commitBody(fields: FieldDescriptor[]): {
    body: CommandBody;
    wire: string;
  } {
    const body = { ...this.wireBody(fields), ...(this.dryRun?.guard ?? {}) };
    // What was typed in a search box helped the dry run find options; the command never takes it.
    if (this.dryRun?.choices.some((c) => c.searchable))
      delete body[CHOICE_SEARCH_FIELD];
    const wire = JSON.stringify(body);
    if (
      this.hasProperty("confirmed") ||
      (this.dryRun?.confirmations.length ?? 0) > 0
    )
      body.confirmed = [...this.confirmed];
    if (this.hasProperty("request_id")) {
      if (!this.requestId || this.requestId.wire !== wire)
        this.requestId = { id: this.newRequestId(), wire };
      body.request_id = this.requestId.id;
    }
    return { body, wire };
  }

  /** A wire path (`lines.1.amount`) as the UI row it came from. */
  private toUiPath(path: string, fields: FieldDescriptor[]): string {
    return uiPathOf(path, this.tables(fields), this.selectedRows);
  }

  // ── Dry run ────────────────────────────────────────────────────────────

  private runDryRun(): void {
    if (this.isDisposed) return;
    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;
    const seq = ++this.seq;
    this.status = "checking";
    this.emit();
    const fields = this.fields();
    this.adapter
      .dryRun(this.endpoint, this.wireBody(fields), controller.signal)
      .then((answer) => {
        if (seq !== this.seq || this.isDisposed) return;
        this.inFlight = null;
        this.acceptDryRun(answer);
      })
      .catch((err: unknown) => {
        if (seq !== this.seq || this.isDisposed || isAbort(err)) return;
        this.inFlight = null;
        console.error("object_ui.command.dry_run_failed", {
          command: this.endpoint.command.id,
          status: err instanceof ApiError ? err.status : null,
        });
        this.status = "check_failed";
        this.banner = { tone: "error", message: "Couldn't check: try again." };
        this.emit();
      });
  }

  /**
   * Each choice's options keep the position they first appeared in for as long as this run is open; an option that
   * arrives later (from a search) goes after the ones already shown, in the owner's order. An owner that re-sorts on
   * every dry run (ticked first, say) therefore cannot move a card under the person's cursor.
   */
  private withStableOptionOrder(answer: DryRun): DryRun {
    const choices = answer.choices.map((choice) => {
      const seen = this.optionOrder[choice.key] ?? [];
      const rank = new Map(seen.map((value, n) => [value, n]));
      const known = choice.options
        .filter((o) => rank.has(o.value))
        .sort((a, b) => rank.get(a.value)! - rank.get(b.value)!);
      const arrived = choice.options.filter((o) => !rank.has(o.value));
      this.optionOrder[choice.key] = [...seen, ...arrived.map((o) => o.value)];
      return { ...choice, options: [...known, ...arrived] };
    });
    return { ...answer, choices };
  }

  private acceptDryRun(offered: DryRun): void {
    const answer = this.withStableOptionOrder(offered);
    this.dryRun = answer;
    // Choices: keep a still-offered pick, else the default; drop the rest.
    const choices: Record<string, ChoiceValue> = {};
    for (const choice of answer.choices) {
      const current = this.choices[choice.key];
      if (choice.multiple) {
        // Picks still offered stay (an emptied list too); a first answer starts from the defaults.
        // A locked-on option (disabled and default) is always in the pick.
        const offered = choice.options.filter(
          (o) => !o.disabled_reason || isLockedOn(o),
        );
        choices[choice.key] = Array.isArray(current)
          ? offered
              .filter((o) => isLockedOn(o) || current.includes(o.value))
              .map((o) => o.value)
          : offered.filter((o) => o.is_default).map((o) => o.value);
        continue;
      }
      const stillValid = choice.options.some(
        (o) => o.value === current && !o.disabled_reason,
      );
      const fallback = choice.options.find(
        (o) => o.is_default && !o.disabled_reason,
      );
      if (current !== undefined && stillValid) choices[choice.key] = current;
      else if (fallback) choices[choice.key] = fallback.value;
    }
    // A pick this answer was checked with and no longer offers (Add extra's "For: its lines" once the service is
    // Delivery) is replaced or dropped, so the answer is about a body the shell no longer holds: check again.
    const isPickReplaced = Object.entries(this.choices).some(
      ([key, held]) => JSON.stringify(choices[key]) !== JSON.stringify(held),
    );
    this.choices = choices;
    // Confirmations: only the ones still asked.
    const asked = new Set(answer.confirmations.map((c) => c.key));
    this.confirmed = new Set([...this.confirmed].filter((k) => asked.has(k)));
    // Prefill, once, under anything already typed.
    let prefilled = false;
    if (!this.isPrefilled && answer.prefill) {
      this.isPrefilled = true;
      for (const [path, value] of Object.entries(answer.prefill)) {
        if (this.touched.has(path) || getAt(this.body, path) !== undefined)
          continue;
        this.body = setAt(this.body, path, value);
        prefilled = true;
      }
      for (const field of this.fields()) {
        if (
          field.kind === "table" &&
          field.table.selectable &&
          !this.selectedRows[field.path]
        )
          this.selectedRows = { ...this.selectedRows, [field.path]: [] };
      }
    }
    this.status = "checked";
    // A prefill the server hasn't seen yet, or a pick it no longer offers: check the body as it now is. (A newly
    // offered choice's default needs no second run: an absent choice is its default.) Picks re-check a bounded
    // number of times in a row, so an owner whose choices never settle can't loop the shell.
    const recheckPicks =
      isPickReplaced && this.pickRechecks < MAX_PICK_RECHECKS;
    this.pickRechecks = recheckPicks ? this.pickRechecks + 1 : 0;
    if (prefilled || recheckPicks) {
      this.runDryRun();
      return;
    }
    this.emit();
  }

  // ── Commit ─────────────────────────────────────────────────────────────

  async commit(): Promise<CommandResult | null> {
    if (!this.snapshot.canCommit) {
      this.showAllErrors = true;
      this.emit();
      return null;
    }
    const fields = this.fields();
    const { body } = this.commitBody(fields);
    this.status = "committing";
    this.banner = null;
    this.emit();
    try {
      const result = await this.adapter.commit(this.endpoint, body);
      if (this.isDisposed) return result;
      this.result = result;
      this.requestId = null;
      this.status = "done";
      this.emit();
      return result;
    } catch (err) {
      if (this.isDisposed) return null;
      this.status = "checked";
      this.showAllErrors = true;
      const logFields = {
        command: this.endpoint.command.id,
        status: err instanceof ApiError ? err.status : null,
        request_id:
          typeof body.request_id === "string" ? body.request_id : null,
      };
      if (err instanceof ApiError) {
        const stale = staleMessage(err);
        if (stale) {
          console.warn("object_ui.command.refused", {
            ...logFields,
            reason: "stale",
          });
          this.banner = { tone: "warning", message: stale };
          this.confirmed = new Set();
          this.runDryRun();
          // `runDryRun` resets the banner only on a failed read; keep the stale sentence.
          this.banner = { tone: "warning", message: stale };
          this.emit();
          return null;
        }
        if (err.status === 409) {
          console.warn("object_ui.command.refused", {
            ...logFields,
            reason: "conflict",
          });
          this.banner = { tone: "error", message: conflictMessage(err) };
          this.emit();
          return null;
        }
        const errors = extractFieldErrors(err);
        if (errors.length > 0) {
          console.warn("object_ui.command.refused", {
            ...logFields,
            reason: "validation",
          });
          this.commitErrors = errors.map((e) => ({
            ...e,
            field: this.toUiPath(e.field, fields),
          }));
          const n = errors.length;
          this.banner = {
            tone: "error",
            message: `${n} ${n === 1 ? "field needs" : "fields need"} fixing.`,
          };
          this.emit();
          return null;
        }
      }
      console.error("object_ui.command.failed", logFields);
      this.banner = {
        tone: "error",
        message: `Couldn't run ${commandTitle(this.endpoint.command.label).toLowerCase()}: try again.`,
      };
      this.emit();
      return null;
    }
  }

  // ── Snapshot ───────────────────────────────────────────────────────────

  private buildSnapshot(): CommandRunSnapshot {
    const fields = this.fields();
    const all = (this.commitErrors ?? this.dryRun?.errors ?? []).map((e) =>
      this.commitErrors ? e : { ...e, field: this.toUiPath(e.field, fields) },
    );
    // An error with no field (the form's, "nothing to change") has no input to touch; with the commit disabled
    // while it stands, waiting for a commit attempt would leave the button greyed out and no reason shown.
    const isTouched = (path: string) =>
      path === "" ||
      this.touched.has(path) ||
      this.touched.has(path.split(".")[0]!);
    // The label of the input a path belongs to: a drawn field, a choice, or a custom widget's path (its schema title).
    const inputs = allFieldsOf(fields);
    const labelOf = (path: string): string | undefined => {
      const input = inputs
        .filter((f) => path === f.path || path.startsWith(`${f.path}.`))
        .sort((a, b) => b.path.length - a.path.length)[0];
      if (input) return input.label;
      const choice = this.dryRun?.choices.find(
        (c) => path === c.key || path.startsWith(`${c.key}.`),
      );
      if (choice) return choice.label;
      const owned = this.ownedPaths.find(
        (p) => path === p || path.startsWith(`${p}.`),
      );
      return owned === undefined
        ? undefined
        : (this.endpoint.command.request_schema?.properties?.[owned]?.title ??
            humanise(owned));
    };
    // An empty input nobody has touched is held back (a fresh form is not a wall of red); anything else shows.
    const valueAt = bodyReader(this.body, this.choices);
    const isHeldBack = (e: FieldError) =>
      !this.showAllErrors &&
      labelOf(e.field) !== undefined &&
      !isTouched(e.field) &&
      isEmptyValue(valueAt(e.field));
    const errors = all.filter((e) => !isHeldBack(e));
    // A field the dry run made required for this record: the commit waits while it is empty, whatever the errors say.
    const hints = this.dryRun?.field_hints ?? {};
    const emptyRequired = inputs.filter(
      (f) =>
        hints[f.path]?.required === true &&
        isShown(f, valueAt) &&
        isEmptyValue(valueAt(f.path)),
    );
    // A keyed-files slot the dry run marks required waits for its file the same way.
    const emptySlots = inputs.flatMap((f) =>
      f.kind === "keyed_files" && isShown(f, valueAt)
        ? (hints[f.path]?.file_slots ?? []).filter(
            (s) =>
              s.required === true &&
              !isChosenFile(valueAt(`${f.path}.${s.key}`)),
          )
        : [],
    );
    const unfilledFields = [
      ...new Set([
        ...all.filter(isHeldBack).map((e) => labelOf(e.field)!),
        ...emptyRequired.map((f) => f.label),
        ...emptySlots.map((s) => s.label),
      ]),
    ];
    const isPending = this.timer !== null || this.inFlight !== null;
    const unsupported = fields.some((f) => f.kind === "unsupported");
    const dryRun = this.dryRun;
    const canCommit =
      dryRun !== null &&
      dryRun.can_commit &&
      emptyRequired.length === 0 &&
      emptySlots.length === 0 &&
      !isPending &&
      !unsupported &&
      this.status === "checked" &&
      this.commitErrors === null &&
      dryRun.confirmations.every((c) => this.confirmed.has(c.key));
    return {
      body: this.body,
      fields,
      dryRun,
      status: this.status,
      isPending,
      seq: this.seq,
      choices: this.choices,
      searches: this.searchesOf(),
      confirmed: this.confirmed,
      lockedPaths: this.lockedPaths,
      selectedRows: this.selectedRows,
      errors,
      unfilledFields,
      banner: this.banner,
      result: this.result,
      canCommit,
    };
  }
}
